import { environment } from 'src/environments/environment';

/**
 * Catálogo central de endpoints (rutas relativas) del backend del Sistema de
 * Inventarios de Cooper.
 *
 * Concentra, agrupadas por dominio funcional, todas las rutas de la API que la
 * aplicación consume. Cada grupo (autorizaciones, facturación, lotes de
 * importación, exportación, cabotaje, barcos, BL, reportes, catálogos, tickets
 * de báscula, citas, notificaciones y DEC) expone las claves de operación
 * (`list`, `get`, `create`, `update`, `delete`, etc.) que se combinan con
 * `url` para construir la URL completa de cada petición HTTP.
 *
 * La propiedad `url` toma su valor del entorno activo (`environment.url`), por
 * lo que el mismo catálogo apunta a desarrollo, QA o producción según la
 * configuración de build.
 */
export const GLOBAL = {
  /** URL base del backend, tomada del entorno activo (`environment.url`). */
  url: environment.url,
  /** Endpoints de gestión de perfiles, usuarios y permisos por menú/opción. */
  authorizations: {
    get: 'user-menu-options-actions',
    getByProfile: 'user-menu-options-actions/',
    update: 'user-menu-options-actions',
    profiles: 'profiles',
    createProfile: 'profiles',
    user: {
      list: 'user',
      create: 'user',
      update: 'user',
      delete: 'user'
    }
  },
  /** Endpoints de generación y descarga de facturas y sus catálogos (CFDI, moneda, condiciones y método de pago). */
  billing: {
    list: 'generate-bill-batch/',
    create: 'generate-bill-batch',
    file: 'download-bill-batch',
    cfdiList: 'catalog-cfdi',
    typeCurrency: 'type-currency',
    paymentConditions: 'payment-conditions',
    paymentMethod: 'payment-method',
  },
  /** Endpoint de constantes del sistema (tarifas, capacidades, porcentajes). */
  constans: 'system-constants',
  /** Endpoints del módulo de lotes de importación: lotes, catálogos, entradas, salidas, abandonos y pedimentos. */
  lotes: {
    list: 'batch',
    listBl: 'batch/',
    create: 'batch',
    update: 'batch',
    delete: 'batch',
    deleteAll: 'batch-pieces',
    getById: 'batch-by-id/',
    createBatchList: 'batch-pieces',
    downloadLayout: 'batch-pieces',
    previousRegistration: 'lot-sample/',
    embargoedMerchandise: 'lot-embargoed-merchandise/',
    locations: 'locations',
    packaging: 'packaging',
    material_group: 'material-group',
    material: 'material/',
    customer: 'customer',
    customerBusinessName: 'business-name/',
    countries: 'countries',
    modulationStatus: 'modulation-type-list',
    customName: 'custom-name-list',
    nationalPorts: 'national-ports-list',
    listTransportLine: 'transport-line',
    file: 'download-lot-file',
    fileBatch: 'download-file-bl',
    entrada: {
      tipoEntrada: 'input-types',
      get: 'merchandise-entry/',
      list: 'merchandise-entry',
      listByBatch: 'merchandise-entry-by-lot/',
      create: 'merchandise-entry',
      update: 'merchandise-entry',
      delete: 'merchandise-entry',
    },
    salida: {
      getSalida: 'merchandise-output/',
      getSalidasByLoteId: 'merchandise-output-by-batch/',
      list: 'merchandise-output',
      create: 'merchandise-output',
      update: 'merchandise-output',
      delete: 'merchandise-output',
      tipoSalida: 'output-type-option',
      tipoSalidaPedimento: 'output-type',
      tipoTransporte: 'transport-type',
      tipoUnidad: 'type-drive',
      fileTicketBascula: 'download-file-weighbridge-ticket',
      fileOrdenTransporte: 'download-file-transportation-order',
      fileOrdenTraspaso: 'download-file-transfer-order',
      fileSinPresentacionFisica:
        'download-file-official-document-authorization',
      fileMerma: 'download-losses-document-file',
      fileAgreementFile: 'merchandise-custom-agreement-file',
      fileRetorno: 'merchandise-return-file',
      fileTransportOrder: 'transport-order-data/',
      fileTransportOrderExpCab: 'transport-order-exp-cab',
      transport: {
        list: 'weight-output-list/',
        file: 'download-file-weighbridge-ticket',
        delete: 'weight-output-weight',
        deleteAll: 'wm-change-weight',
        create: 'weight-output-weight',
        update: 'weight-output-weight',
        deleteSlabs: 'slab-weight'
      },
      slab: {
        layout: 'slab-list-layout',
        list: 'slab-list/',
        createList: 'slab-list',
        update: 'slab-list',
        delete: 'slab-list',
        deleteSlabList: 'delete-all-slabs/',
        activeSlabs: 'slab-list-catalog/',
        downloadLetterSlab: 'slab-output-membretate'
      }
    },
    abandono: {
      get: 'abandonment-merchandise/',
      list: 'abandonment-merchandise/',
      date: 'abandonment-merchandise-date/',
      file: 'download-file-abandonment-merchandise',
      create: 'abandonment-merchandise',
      update: 'abandonment-merchandise',
      delete: 'abandonment-merchandise',
    },
    pedimento: {
      keyList: 'custom-entry-key-list',
      requestType: 'request-type',
      activeList: 'customs-entry-active/',
      get: 'customs-entry/',
      list: 'customs-entry',
      create: 'customs-entry',
      update: 'customs-entry',
      delete: 'customs-entry',
      uploadFile: 'upload-file-customs',
      downloadFile: 'download-file-customs',
    },
  },
  /** Endpoints del módulo de lotes de exportación: lotes, entradas, cartas membretadas, salidas y pedimentos. */
  loteExportacion: {
    create: 'export-batch',
    update: 'export-batch',
    delete: 'export-batch',
    list: 'export-batch',
    get: 'export-batch/',
    file: 'download-export-file-bl',
    fileEmbargoedMerchandise: 'download-export-lot-file',
    previousRegistration: 'export-lot-sample/',
    embargoedMerchandise: 'export-lot-embargoed-merchandise/',
    entradas: {
      create: 'export-entries',
      update: 'export-entries',
      delete: 'export-entries',
      list: 'export-entries',
      listByBatch: 'export-entries-by-batch/',
      entriesType: 'entry-type-list',
      get: 'export-entries/',
      file: 'download-file-export-entries',
      cartaMembretada: {
        create: 'generate-membrated-document',
        get: 'download-membrated-document'
      },
      membratedLetter: {
        listByBatch: 'download-membrated-document/',
        tonnageAvailableByBatch: 'generate-membrated-document/',
        create: 'generate-membrated-document',
        delete: 'generate-membrated-document-delete/',
        get: 'download-membrated-document'
      },
      transport: {
        list: 'export-entry-weight/',
        file: 'download-file-export-entries',
        delete: 'export-entry-weight',
        deleteAll: 'wm-change-weight-entry-exp',
        create: 'export-entry-weight',
        update: 'export-entry-weight',
      }
    },
    salidas: {
      create: 'export-outputs',
      update: 'export-outputs',
      delete: 'export-outputs',
      get: 'export-outputs/',
      listByBatch: 'export-outputs-by-batch/',
      file: 'download-file-export-outputs',
      fileWithdrawal: 'download-file-export-outputs-withdrawal',
      fileMerma: 'export-losses-file',
      fileAgreement: 'download-export-agreement',
      tipos: 'export-output-type',
      batchesAvailableTransfer: 'export-batch-list/',
      asignarBarco: 'export-boat-batch-list/',
      transport: {
        list: 'export-weight-output/',
        delete: 'export-weight-output',
        deleteAll: 'wm-change-weight-output-exp',
        create: 'export-weight-output',
        update: 'export-weight-output',
      },
    },
    pedimento: {
      listByShip: 'export-customs-entry/',
      listByBatch: 'export-customs-withdrawal/',
      activeList: 'export-customs-entry-active/',
      list: 'export-customs-entry',
      create: 'export-customs-entry',
      update: 'export-customs-entry',
      delete: 'export-customs-entry',
      downloadFile: 'download-file-export-customs',
    }
  },
  /** Endpoints del módulo de lotes de cabotaje: lotes, entradas, cartas membretadas y salidas. */
  loteCabotaje: {
    create: 'cabotage-batch',
    update: 'cabotage-batch',
    delete: 'cabotage-batch',
    list: 'cabotage-batch',
    listByShip: 'cabotage-imp-batch/',
    createImp: 'cabotage-imp-batch',
    updateImp: 'cabotage-imp-batch',
    deleteImp: 'cabotage-imp-batch',
    get: 'cabotage-batch/',
    file: 'download-file-cabotage-bl',
    fileEmbargoedMerchandise: 'download-cabotage-lot-file',
    previousRegistration: 'cabotage-lot-sample/',
    embargoedMerchandise: 'cabotage-lot-embargoed-merchandise/',
    annexFile: 'download-cabotage-annex-file',
    loteBarco: 'export-boat-batch-detail/',
    entradas: {
      create: 'cabotage-entrance',
      update: 'cabotage-entrance',
      delete: 'cabotage-entrance',
      list: 'cabotage-entrance',
      listByBatch: 'cabotage-entrance-by-batch/',
      get: 'cabotage-entrance/',
      file: 'download-cabotage-entrance-file',
      cartaMembretada: {
        create: 'generate-cabotage-membrated-document',
        get: 'download-cabotage-membrated-document'
      },
      membratedLetter: {
        listByBatch: 'download-cabotage-membrated-document/',
        tonnageAvailableByBatch: 'generate-cabotage-membrated-document/',
        create: 'generate-cabotage-membrated-document',
        delete: 'generate-cabotage-membrated-document-delete/',
        get: 'download-cabotage-membrated-document'
      },
      transport: {
        list: 'cabotage-entry-weight/',
        file: 'download-cabotage-entrance-file',
        delete: 'cabotage-entry-weight',
        deleteAll: 'wm-change-weight-entry-cab',
        create: 'cabotage-entry-weight',
        update: 'cabotage-entry-weight',
      }
    },
    salidas: {
      create: 'cabotage-outputs',
      update: 'cabotage-outputs',
      delete: 'cabotage-outputs',
      get: 'cabotage-outputs/',
      listByBatch: 'cabotage-outputs-by-batch/',
      file: 'download-cabotage-outputs-file',
      fileWithdrawal: 'download-cabotage-outputs-file-withdrawal',
      fileMerma: 'cabotage-losses-file',
      fileAgreement: 'download-cabotage-agreement',
      tipos: 'export-output-type',
      batchesAvailableTransfer: 'cabotage-batch-list/',
      asignarBarco: 'export-boat-batch-list/',
      transport: {
        list: 'cabotage-weight-output/',
        delete: 'cabotage-weight-output',
        deleteAll: 'wm-change-weight-output-cab',
        create: 'cabotage-weight-output',
        update: 'cabotage-weight-output',
      },
    },
  },
  /** Endpoints de barcos: listados, detalle, balance, registros de muestreo y regla 238. */
  barco: {
    list: 'boats-list',
    listByYear: 'boats-list/',
    get: 'boat-detail/',
    listByModule: 'boat-list/',
    cabotageList: 'cabotage-outputs-boats-list',
    balance: 'ship-adjust',
    samplingRecord: {
      list: 'sample-exp/',
      get: 'sample-exp/',
      create: 'sample-exp',
      update: 'sample-exp',
      delete: 'sample-exp'
    },
    rule238: {
      get: 'ship-rule-two-three-eight/',
      create: 'ship-rule-two-three-eight',
      update: 'ship-rule-two-three-eight',
      delete: 'ship-rule-two-three-eight',
      embargoed: {
        update: 'insert-embargoed-rule',
        delete: 'insert-embargoed-rule',
        downloadFile: 'download-embargoed-rule',
      }
    }
  },
  /** Endpoints de Bill of Lading (BL): listado, BL máster, detalles, clientes y archivos. */
  bl: {
    list: 'bl',
    bl_master: 'bl-master/',
    bl_masterCreate: 'bl-master',
    bl_masterUpdate: 'bl-master',
    bl_details: 'bl-details/',
    bl_customer: 'bl-customer/',
    file: {
      get: 'download-file-boat',
      create: 'upload-file-boat',
    },
  },
  /** Endpoints de reportes: catálogos de filtros, datos y descargas (Excel/PDF). */
  report: {
    list: 'report-type-list',
    enclosureTypes: 'enclosure-type-list',
    patios: 'locations',
    boatList: 'boat-list-by-module',
    customers: 'customer-by-module',
    materials: 'material-by-module/',
    allMaterials: 'material-by-module',
    materialsWithFilters: 'material-by-module',
    modules: 'system-modules-list',
    data: 'reports-excel',
    downloadExcel: 'download-reports-excel',
    downloadAduanaPDF: 'custom-report',
    batches: 'batch-type-list',
    transporType: 'transport-type',
    unitType: 'type-drive',
    ctsList: 'abandonment-cts-list',
    officeList: 'abandonment-office-list',
    rule238: 'type-report-two-three-eight',
    ticketsForPDFReport: 'weighing-machine-report'
  },
  /** Endpoints de administración de catálogos y sus ítems (clientes y razones sociales). */
  catalog: {
    list: 'catalogs-list',
    items: 'catalog-management/',
    childItems: 'catalog-management/',
    createItem: 'catalog-management',
    updateItem: 'catalog-management',
    deleteItem: 'catalog-management',
    customer: {
      listByMuelles: 'customer/1',
      create: 'customer',
      delete: 'customer',
    },
    bussinessName: {
      get: 'business-name-by-id/',
      create: 'customer-business-name',
      delete: 'customer-business-name',
    }
  },
  /** Endpoints de tickets de báscula: tickets, catálogos, camiones, tara y peso de origen. */
  scaleTicket: {
    list: 'weighing-machine',
    boatsByModule: 'boat-list-by-module/',
    weighingMachinee: 'scale-catalog',
    originTicket: 'original-scale',
    supervisors: 'scale-supervisors',
    modules: 'scale-operations',
    get: 'weighing-machine/',
    getByDate: 'weighing-machine-by-date/',
    create: 'weighing-machine',
    update: 'weighing-machine',
    delete: 'weighing-machine',
    download: 'download-weighing-machine-ticket',
    downloadGenericPDF: 'generic-ticket',
    sybasa: 'wmsibasa',
    toggleInternalExternalTrucks: 'scale-edition-permissions-weight',
    truck: {
      list: 'trucks',
      create: 'trucks',
      update: 'trucks',
      delete: 'trucks'
    },
    tareWeight: {
      list: 'tare-weight/',
      create: 'tare-weight',
      update: 'tare-weight',
      delete: 'tare-weight'
    },
    originWeight: {
      list: 'origin-weight/',
      create: 'origin-weight',
      update: 'origin-weight',
      delete: 'origin-weight'
    },
    bulkUpdate: {
      list: 'weighing-machine/bulk-update-filter',
      update: 'weighing-machine/bulk-update'
    }
  },
  /** Endpoints de toneladas por patio (ubicaciones). */
  tonsPerYard: {
    list: 'locations',
    delete: 'locations/location_delete/'
  },
  /** Endpoints de citas: solicitudes de cliente, asignación, cancelación y sincronización con ASIPONA. */
  appointment: {
    appointmentRequest: {
      get: 'client-appointments/',
      list: 'client-appointments',
      create: 'client-appointments',
      update: 'client-appointments',
      delete: 'client-appointments'
    },
    listAllAppointments: 'appointments',
    assignAnAppointment: 'appointments',
    cancel: 'reject-appointment',
    sendAsipona: 'appointments',
    cancelAsipona: 'appointments',
    get: 'appointments/',
    getPendingAppointments: 'appointments-pending-sse',
    catalogs: 'asipona-catalogs',
    getByAppointmentId: 'dec'
  },
  /** Endpoints de notificaciones: listado, stream de no leídas y marcado como leídas. */
  notification: {
    list: 'notifications',
    unreadStream: 'notifications/stream',
    markAsRead: 'notifications/mark-read',
    markAllAsRead: 'notifications/mark-all-read'
  },
  /** Endpoints del módulo DEC: consulta, listado y catálogos de acceso a terminal/recinto. */
  dec: {
    get: 'dec/',
    list: 'dec',
    terminalAccess: 'asipona-access-catalog',
    entryExitAccess: 'dec-terminal-event',
  }
};
